import type { ReactElement } from 'react';
import { useState, useEffect } from 'react';

import { ApiRoute } from 'config';
import { put } from 'utils/network';
import useCurrentUser from 'hooks/useCurrentUser';

import IApiResponse from 'models/IApiResponse';

import { Component as AppleIcon } from 'icons/icon-social--apple.svg';
import { Component as FacebookIcon } from 'icons/icon-social--facebookbtn.svg';
import { Component as GoogleIcon } from 'payment-img/google-pay/google-pay-icon.svg';

import ValidableControl from 'components/shared/ValidableControl';
import Button from 'react-bootstrap/Button';
import Modal from 'components/shared/Modal';

type Props = {
  show: boolean;
  onClose: () => void;
};

function AccountSettingsModal({ show, onClose }: Props): ReactElement {
  const { currentUser, reload: reloadUser } = useCurrentUser();
  const { email: initialEmail, generated_password: noCurrentPasswordRequired } = currentUser;
  const [email, setEmail] = useState(initialEmail);
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [errors, setErrors] = useState({});

  const getErrorMsg = (attr) => (errors[attr] ? errors[attr][0] : '');
  const isInvalid = (attr) => !!errors[attr];

  const submit = async () => {
    const { id: userId } = currentUser;
    const response = await put<IApiResponse>(`${ApiRoute.USERS}/${userId}`, {
      user: {
        email,
        password,
        current_password: currentPassword,
      },
    });

    if (response.status === 422) {
      // @ts-expect-error TODO: fix null safety
      setErrors(response.parsedBody);
    } else {
      reloadUser();
      onClose();
    }
  };

  useEffect(() => {
    setErrors({});

    if (password && password.length < 8) {
      setErrors({
        password: ['* Password must be at least 8 characters'],
      });
    }

    if (
      password && passwordConfirmation
      && password !== passwordConfirmation
    ) {
      setErrors({
        password: ['* Paswords don’t match'],
        password_confirmation: ['* Paswords don’t match'],
      });
    }
  }, [password, passwordConfirmation]);

  return (
    <Modal
      show={show}
      onClose={onClose}
      className="c-modal-account-settings"
      header={(
        <Modal.Header className="c-heading--brand-6" center={false}>
          Edit Account Settings
        </Modal.Header>
      )}
      body={(
        <div>
          <ValidableControl
            label="Email"
            htmlFor="edit-user-email"
            isInvalid={isInvalid('email')}
            errorMessage={getErrorMsg('email')}
          >
            <input
              id="edit-user-email"
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </ValidableControl>
          {!noCurrentPasswordRequired && (
            <ValidableControl
              label="Current Password"
              htmlFor="user_current_password"
              isInvalid={isInvalid('current_password')}
              errorMessage={getErrorMsg('current_password')}
            >
              <input
                id="user_current_password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
            </ValidableControl>
          )}
          <ValidableControl
            label="New Password"
            htmlFor="user_password"
            isInvalid={isInvalid('password')}
            errorMessage={getErrorMsg('password')}
          >
            <input
              id="user_password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </ValidableControl>
          <ValidableControl
            label="Confirm New Password"
            htmlFor="user_password_confirmation"
            isInvalid={isInvalid('password_confirmation')}
            errorMessage={getErrorMsg('password_confirmation')}
          >
            <input
              id="user_password_confirmation"
              type="password"
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
            />
          </ValidableControl>
          <div className="c-form-group">
            <a
              className="c-button c-button--facebook d-block"
              data-cy="connect-with-facebook"
              rel="nofollow"
              href="/users/auth/facebook"
            >
              <FacebookIcon />
              Connect With Facebook
            </a>
          </div>
          <div className="c-form-group">
            <a
              className="c-button c-button--google d-block"
              data-cy="connect-with-google"
              href="/users/auth/google_oauth2"
            >
              <GoogleIcon />
              Connect With Google
            </a>
          </div>
          <div className="c-form-group">
            <a
              className="c-button c-button--apple d-block"
              data-cy="connect-with-apple"
              href="/users/auth/apple"
            >
              <AppleIcon />
              Connect With Apple
            </a>
          </div>
        </div>
      )}
      footer={(
        <>
          <Button
            className="c-button c-button--primary-link"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            className="c-button c-button--primary"
            disabled={!(
              email
              && (currentPassword || noCurrentPasswordRequired)
              && password === passwordConfirmation
              && (!password || password.length >= 8)
            )}
            onClick={submit}
          >
            Update
          </Button>
        </>
      )}
    />
  );
}

export default AccountSettingsModal;
